import { z } from 'zod';
import { CHILLDOC_MAX_BYTES, DOCUMENT_FORMATS } from './chilldoc.js';
import { PresignedUpload } from './file.js';
/**
 * Chia sẻ tài liệu cá nhân — APM-SHR-b…e, docs/02-sprint-plan.md "Cấu hình & chia sẻ tài liệu
 * cá nhân". Một nguồn sự thật cho API và popup "Cấu hình và chia sẻ".
 */
/**
 * `edit` để dành: hiện trong mọi ô chọn nhưng khoá, API từ chối (người dùng chốt 2026-09-29).
 *
 * `suggest` (APM-CD2-i, plan-chilldoc-v2 §7.2): người nhận mở `.chilldoc` bằng trình soạn ở chế độ
 * đề xuất **bắt buộc**; mỗi lần Lưu là một phiên bản mới trong nhóm phiên bản của chủ tệp. Chỉ
 * cấp theo **người** (cần biết ai đề xuất) và chỉ cho `.chilldoc` (chỉ trình soạn Chill có chế độ
 * đề xuất) — link chia sẻ dùng `LINK_SHARE_MODES`.
 */
export const DOCUMENT_SHARE_MODES = ['copy', 'read', 'suggest', 'edit'];
export const ENABLED_SHARE_MODES = ['copy', 'read', 'suggest'];
export const EnabledShareMode = z.enum(ENABLED_SHARE_MODES);
/** Kiểu của **link**: không có `suggest` — link công khai không có người để ghi "Đề xuất của …". */
export const LINK_SHARE_MODES = ['copy', 'read'];
export const LinkShareMode = z.enum(LINK_SHARE_MODES);
/** Phần nội dung người nhận thấy. `text` luôn có — không có chữ thì không còn tài liệu. */
export const DOCUMENT_SHARE_SCOPES = ['text', 'images', 'tables', 'presentation'];
export const DocumentShareScope = z
    .array(z.enum(DOCUMENT_SHARE_SCOPES))
    .min(1)
    .refine((items) => items.includes('text'), { message: 'Phạm vi luôn có văn bản.' })
    .transform((items) => DOCUMENT_SHARE_SCOPES.filter((item) => items.includes(item)));
/** Chia sẻ toàn bộ ⇒ link luôn đọc bản mới nhất; thiếu phần nào ⇒ link trỏ bản chụp đã lọc. */
export function isFullShareScope(scope) {
    return DOCUMENT_SHARE_SCOPES.every((item) => scope.includes(item));
}
/** Ai mở được link. `public` không bao giờ có quyền sửa (kể cả khi `edit` có sau này). */
export const DOCUMENT_SHARE_AUDIENCES = ['org', 'chillpm', 'public'];
export const DocumentShareAudience = z.enum(DOCUMENT_SHARE_AUDIENCES);
/** Lựa chọn nhanh của ô thời hạn; "Chọn ngày" gửi thẳng `expiresAt`. */
export const SHARE_EXPIRY_DAYS = [1, 7, 30];
/** Trần thời hạn đặt tay: một năm — "không hạn" là lựa chọn riêng, có chủ đích. */
export const SHARE_MAX_EXPIRY_DAYS = 366;
/** Bản chụp đã lọc do trình duyệt chủ tệp PUT lên (luật #3 — không đi qua Node). */
export const ShareSnapshotRef = z.object({
    key: z.string().trim().min(1).max(512),
    /** Hàng tệp bản chụp dựng từ đó — để biết link đã cũ chưa. */
    sourceFileId: z.uuid(),
    /** SHA-256 (hex) của nội dung đã tải lên — bắt buộc với bản sạch (APM-CMT-d): bản sao ghi nó. */
    checksumSha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/)
        .optional(),
});
export const ShareSnapshotPresignInput = z.object({
    sizeBytes: z.number().int().positive().max(CHILLDOC_MAX_BYTES),
});
export const ShareSnapshotPresign = z.object({
    key: z.string(),
    upload: PresignedUpload.omit({ fileId: true }),
});
export const CreateShareLinkInput = z.object({
    mode: LinkShareMode,
    scope: DocumentShareScope,
    audience: DocumentShareAudience,
    /** `null` = không hạn. */
    expiresAt: z.iso.datetime().nullable(),
    /** Bắt buộc khi phạm vi không đủ — xem `isFullShareScope`. */
    snapshot: ShareSnapshotRef.optional(),
});
export const DocumentShareLinkItem = z.object({
    id: z.uuid(),
    /** Link đầy đủ — chép lại được bất cứ lúc nào (token mã hoá ở server, như link mời). */
    url: z.string(),
    mode: EnabledShareMode,
    scope: z.array(z.enum(DOCUMENT_SHARE_SCOPES)),
    audience: DocumentShareAudience,
    expiresAt: z.iso.datetime().nullable(),
    expired: z.boolean(),
    openCount: z.number().int().nonnegative(),
    lastOpenedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    /** Chia sẻ toàn bộ ⇒ luôn bản mới nhất, không bao giờ cũ. */
    full: z.boolean(),
    /** Bản chụp dựng từ một bản cũ hơn bản mới nhất của tài liệu. */
    stale: z.boolean(),
    snapshotAt: z.iso.datetime().nullable(),
});
/**
 * "Bản sạch" của tài liệu đang chia sẻ — APM-CMT-d: bản `.chilldoc` không bình luận mà link toàn
 * bộ, quyền theo người và "Lưu bản sao" đọc thay cho tệp gốc. Server không đọc nội dung (luật #3)
 * nên trình duyệt chủ tệp dựng và tải lên (`PUT /me/files/:id/shares/clean-copy`).
 */
export const ShareCleanCopyState = z.object({
    /** `.chilldoc` còn link toàn bộ hoặc quyền theo người ⇒ cần bản sạch. */
    needed: z.boolean(),
    /** Bản sạch dựng từ đúng bản mới nhất. `needed && !fresh` ⇒ nên tải lại. */
    fresh: z.boolean(),
    updatedAt: z.iso.datetime().nullable(),
});
export const DocumentShareLinks = z.object({
    items: z.array(DocumentShareLinkItem),
    /** Hàng tệp mới nhất của tài liệu — bản chụp "Cập nhật nội dung" dựng từ đây. */
    latestFileId: z.uuid(),
    cleanCopy: ShareCleanCopyState,
});
// ---------------------------------------------------------------------------
// Chia sẻ với người (APM-SHR-c)
// ---------------------------------------------------------------------------
/** Tìm nhanh thành viên tổ chức đang đứng cho ô mời người. */
export const OrgMemberSearchQuery = z.object({
    q: z.string().trim().max(100).default(''),
});
export const OrgMemberHit = z.object({
    userId: z.uuid(),
    name: z.string(),
    email: z.string(),
});
export const OrgMemberHits = z.object({ items: z.array(OrgMemberHit) });
/** Mời theo người trong tổ chức (`userId`) hoặc theo email (người ngoài tổ chức). */
export const CreateShareGrantInput = z
    .object({
    userId: z.uuid().optional(),
    email: z.email().trim().toLowerCase().max(320).optional(),
    permission: EnabledShareMode,
})
    .refine((input) => (input.userId ? 1 : 0) + (input.email ? 1 : 0) === 1, {
    message: 'Chọn một người trong tổ chức hoặc nhập một email.',
});
export const UpdateShareGrantInput = z.object({ permission: EnabledShareMode });
/** Một dòng theo **email** — một email nhiều tài khoản (nhiều tổ chức) vẫn là một người. */
export const DocumentShareGrantItem = z.object({
    /** Id của lượt cấp đại diện; đổi quyền / thu hồi áp cho mọi tài khoản của email. */
    id: z.uuid(),
    email: z.string(),
    name: z.string(),
    permission: EnabledShareMode,
    /** Người ngoài tổ chức của chủ tệp. */
    external: z.boolean(),
    createdAt: z.iso.datetime(),
});
export const DocumentShareGrants = z.object({ items: z.array(DocumentShareGrantItem) });
/** Mục "Được chia sẻ với tôi" ở trang Tệp. */
export const SharedWithMeItem = z.object({
    grantId: z.uuid(),
    name: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    ownerName: z.string(),
    permission: EnabledShareMode,
    sharedAt: z.iso.datetime(),
});
export const SharedWithMe = z.object({ items: z.array(SharedWithMeItem) });
/**
 * Lượt chia sẻ **cho chính người gọi** ứng với một tệp — cho link `/files?preview=<id>` mà chủ tệp
 * chép gửi người nhận (APM-PVLINK). `null` = không có (tệp không chia sẻ cho họ, hay không tồn tại):
 * hai ca không phân biệt được, có chủ đích. Bọc trong object để "không có" là một giá trị.
 */
export const SharedGrantOfFile = z.object({ grantId: z.uuid().nullable() });
// ---------------------------------------------------------------------------
// Mở tài liệu được chia sẻ (APM-SHR-d)
// ---------------------------------------------------------------------------
export const SharedDocument = z.object({
    name: z.string(),
    /** `null` = tệp tải lên (PDF, ảnh, Office…) — trình xem chọn theo tên + `mimeType` (APM-SHR-g). */
    format: z.enum(DOCUMENT_FORMATS).nullable(),
    mimeType: z.string().nullable(),
    ownerName: z.string(),
    mode: EnabledShareMode,
    scope: z.array(z.enum(DOCUMENT_SHARE_SCOPES)),
    /** Presigned GET của bản mới nhất, hoặc của bản chụp đã lọc. */
    contentUrl: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    /** Bản chụp dựng từ bản cũ — người xem được báo "nội dung đã cũ". */
    stale: z.boolean(),
    /** `mode = copy` **và** người xem đã đăng nhập. */
    canCopy: z.boolean(),
    expiresAt: z.iso.datetime().nullable(),
    /**
     * Hàng tệp đang hiện — **chỉ** với quyền `suggest` (APM-CD2-i): lượt lưu đề xuất nối tiếp đúng
     * bản này, chủ đã lưu bản khác thì server trả 409 thay vì đè. `null` với mọi kiểu khác.
     */
    fileId: z.uuid().nullable(),
});
/** Ảnh `apm-file:` trong tài liệu được chia sẻ — server chỉ ký cho id có trong tài liệu. */
export const SharedFileUrlsInput = z.object({ ids: z.array(z.uuid()).max(200) });
export const SharedFileUrls = z.object({ urls: z.record(z.string(), z.string().nullable()) });
export const SharedCopyResult = z.object({ fileId: z.uuid(), name: z.string() });
// ---------------------------------------------------------------------------
// Đề xuất trên tài liệu được chia sẻ (APM-CD2-i, plan-chilldoc-v2 §7.2)
// ---------------------------------------------------------------------------
/**
 * Xin presigned PUT cho một lượt lưu đề xuất. `baseFileId` là `SharedDocument.fileId` của bản
 * người đề xuất đang soạn — lệch bản mới nhất ⇒ 409 `stale_version`.
 */
export const SuggestionUploadInput = z.object({
    baseFileId: z.uuid(),
    sizeBytes: z.number().int().positive().max(CHILLDOC_MAX_BYTES),
    checksumSha256: z.string().regex(/^[0-9a-f]{64}$/),
});
/** Xác nhận bản đã PUT thành phiên bản mới của tài liệu chủ. */
export const SuggestionCommitInput = z.object({
    baseFileId: z.uuid(),
    pendingFileId: z.uuid(),
});
export const SuggestionSaved = z.object({
    /** Hàng tệp của phiên bản vừa tạo — lần lưu sau nối tiếp nó. */
    fileId: z.uuid(),
    versionNo: z.number().int().positive(),
});
//# sourceMappingURL=document-share.js.map